import React, { useState, useRef, useEffect } from 'react';
import './index.less';
import { Form, Input, Button, Select, message, Modal, Spin } from 'antd';
import { PlusOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { OCTrack } from '../../types';
import FramePlayer from '../FramePlayer';

interface RecordModuleProps {
    onClose: () => void;
    bpm: 100 | 120;
    ocTrack: OCTrack | null;
}

const RecordModule: React.FC<RecordModuleProps> = ({ onClose, bpm, ocTrack }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [progress, setProgress] = useState(0);
    const [recordingComplete, setRecordingComplete] = useState(false);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const prepareDuration = 2000; // 预备 2s
    const recordingDuration = bpm === 100 ? 9600 : 8000; // 实际录音时长（毫秒）
    const totalDuration = prepareDuration + recordingDuration; // 总时长
    const beatsCount = 16; // 4/4拍，每小节3个拍点，共4小节

    // const [countDown, setCountDown] = useState(2);

    const [isBeatActive, setIsBeatActive] = useState(false);
    const [activeBeat, setActiveBeat] = useState<number | null>(null);
    const [isPlayingBack, setIsPlayingBack] = useState(false);
    const [prepareCountdown, setPrepareCountdown] = useState(2);

    const OC_PLAY_OFFSET_MS = 200;

    // utility to encode AudioBuffer to WAV (PCM 16bit)
    const audioBufferToWav = (buffer: AudioBuffer): Blob => {
        const numOfChan = buffer.numberOfChannels;
        const sampleRate = buffer.sampleRate;
        const format = 1; // PCM
        const bitDepth = 16;

        let result;
        if (numOfChan === 2) {
            const interleaved = interleave(buffer.getChannelData(0), buffer.getChannelData(1));
            result = encodeWav(interleaved, numOfChan, sampleRate, bitDepth);
        } else {
            result = encodeWav(buffer.getChannelData(0), numOfChan, sampleRate, bitDepth);
        }
        return new Blob([result], { type: 'audio/wav' });
    };

    const interleave = (inputL: Float32Array, inputR: Float32Array) => {
        const length = inputL.length + inputR.length;
        const result = new Float32Array(length);
        let index = 0, inputIndex = 0;
        while (index < length) {
            result[index++] = inputL[inputIndex];
            result[index++] = inputR[inputIndex];
            inputIndex++;
        }
        return result;
    };

    const encodeWav = (samples: Float32Array, numChannels: number, sampleRate: number, bitDepth: number) => {
        const bytesPerSample = bitDepth / 8;
        const blockAlign = numChannels * bytesPerSample;

        const buffer = new ArrayBuffer(44 + samples.length * bytesPerSample);
        const view = new DataView(buffer);

        /* RIFF identifier */ writeString(view, 0, 'RIFF');
        /* file length */ view.setUint32(4, 36 + samples.length * bytesPerSample, true);
        /* RIFF type */ writeString(view, 8, 'WAVE');
        /* format chunk identifier */ writeString(view, 12, 'fmt ');
        /* format chunk length */ view.setUint32(16, 16, true);
        /* sample format (raw) */ view.setUint16(20, 1, true);
        /* channel count */ view.setUint16(22, numChannels, true);
        /* sample rate */ view.setUint32(24, sampleRate, true);
        /* byte rate (sample rate * block align) */ view.setUint32(28, sampleRate * blockAlign, true);
        /* block align (channel count * bytes per sample) */ view.setUint16(32, blockAlign, true);
        /* bits per sample */ view.setUint16(34, bitDepth, true);
        /* data chunk identifier */ writeString(view, 36, 'data');
        /* data chunk length */ view.setUint32(40, samples.length * bytesPerSample, true);
        // write samples
        floatTo16BitPCM(view, 44, samples);
        return buffer;
    };

    const writeString = (view: DataView, offset: number, str: string) => {
        for (let i = 0; i < str.length; i++) {
            view.setUint8(offset + i, str.charCodeAt(i));
        }
    };

    const floatTo16BitPCM = (output: DataView, offset: number, input: Float32Array) => {
        for (let i = 0; i < input.length; i++, offset += 2) {
            let s = Math.max(-1, Math.min(1, input[i]));
            s = s < 0 ? s * 0x8000 : s * 0x7FFF;
            output.setInt16(offset, s, true);
        }
    };

    async function trimBlob(blob: Blob): Promise<Blob> {
        const arrayBuf = await blob.arrayBuffer();
        const context = new AudioContext();
        const audioBuf = await context.decodeAudioData(arrayBuf.slice(0));
        const startOffset = OC_PLAY_OFFSET_MS / 1000;
        const startFrame = Math.floor(startOffset * audioBuf.sampleRate);
        const trimmedLen = audioBuf.length - startFrame;
        const trimmed = context.createBuffer(audioBuf.numberOfChannels, Math.max(trimmedLen,0), audioBuf.sampleRate);
        for (let ch = 0; ch < audioBuf.numberOfChannels; ch++) {
            audioBuf.copyFromChannel(trimmed.getChannelData(ch), ch, startFrame);
        }
        return audioBufferToWav(trimmed);
    }

    const startRecording = async () => {
        try {
            // 预先获取麦克风权限
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeType = getSupportedMimeType();
            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: mimeType,
                audioBitsPerSecond: 128000 // 设置比特率为 128kbps
            });
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            setIsRecording(true);

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
                const url = URL.createObjectURL(audioBlob);
                setAudioUrl(url);
                setRecordingComplete(true);
            };

            // 进度条 & 节拍逻辑
            let elapsed = 0;
            setProgress(0);
            progressIntervalRef.current = setInterval(() => {
                elapsed += 100;
                const progressPercent = Math.min((elapsed / totalDuration) * 100, 100);

                // 处理节拍效果（只在录音阶段）
                if (elapsed > prepareDuration) {
                    const effectiveProgressRatio = (elapsed - prepareDuration) / recordingDuration; // 0-1
                    const beatIdx = Math.floor(effectiveProgressRatio * beatsCount); // 0-15
                    const beatTime = beatIdx * (recordingDuration / beatsCount);
                    // 若刚进入新的 beat 区间
                    if (beatIdx !== activeBeat) {
                        setActiveBeat(beatIdx);
                        setIsBeatActive(true);
                        setTimeout(() => setIsBeatActive(false), 100);
                    }
                }

                if (elapsed <= prepareDuration) {
                    const remain = Math.ceil((prepareDuration - elapsed) / 1000);
                    setPrepareCountdown(remain > 0 ? remain : 0);
                }

                setProgress(progressPercent);
            }, 100);

            // 在预备结束后开始录音
            setTimeout(() => {
                mediaRecorder.start();
            }, prepareDuration);

            // 在总时长结束后停止
            setTimeout(() => {
                clearInterval(progressIntervalRef.current!);
                if (mediaRecorderRef.current?.state === 'recording') {
                    mediaRecorderRef.current.stop();
                    stream.getTracks().forEach(track => track.stop());
                }
                setIsRecording(false);
                setProgress(100);

            // 录音完成统计
            if (typeof gtag !== 'undefined') {
                gtag('event', 'recording_complete', {
                    event_category: 'audio',
                    event_label: ocTrack?.hashValue || 'record_module',
                    value: 1
                });
            }

            }, totalDuration);

        } catch (error) {
            console.error('Error starting recording:', error);
            message.error('无法访问麦克风，请确保已授予权限。');
        }
    };

    const getSupportedMimeType = () => {
        const types = [
            'audio/webm',
            'audio/webm;codecs=opus',
            'audio/ogg;codecs=opus',
            'audio/mp4',
            'audio/wav'
        ];
        
        for (const type of types) {
            if (MediaRecorder.isTypeSupported(type)) {
                console.log('Using audio format:', type);
                return type;
            }
        }
        
        throw new Error('No supported audio format found');
    };

    const handleUpload = async () => {
        console.log('handleUpload', audioUrl)
        if (!audioUrl) return;

        try {
            // 裁剪前 0.2s
            const trimmedBlob = await trimBlob(audioChunksRef.current[0]);

            const formData = new FormData();
            formData.append('audio', trimmedBlob, 'trimmed.wav');
            formData.append('hash', ocTrack?.hashValue || '');
            formData.append('bpm', bpm.toString());

            const response = await fetch('/api/v1/incredibox/oc/audio', {
                method: 'POST',
                body: formData
            });

            const resJson = await response.json();
            if (resJson.success) {
                message.success('上传成功！');
                // 可以在此更新 ocTrack 链接等信息
            } else {
                message.error(resJson.message || '上传失败');
            }
        } catch (error) {
            console.error('Error uploading audio:', error);
            alert('上传失败，请重试。');
        }
    };

    const startPlayback = () => {
        if (!audioUrl) return;

        const audio = new Audio(audioUrl);
        setIsPlayingBack(true);
        setProgress(0);

        let elapsed = 0;

        progressIntervalRef.current = setInterval(() => {
            elapsed += 100;

            // 总进度按 totalDuration (prepare+record) 计算
            const progressPercent = Math.min((elapsed / totalDuration) * 100, 100);

            // 节拍激活（只在录音阶段）
            if (elapsed > prepareDuration) {
                const effectiveRatio = (elapsed - prepareDuration) / recordingDuration;
                const beatIdx = Math.floor(effectiveRatio * beatsCount);
                if (beatIdx !== activeBeat) {
                    setActiveBeat(beatIdx);
                    setIsBeatActive(true);
                    setTimeout(() => setIsBeatActive(false), 100);
                }
            }

            setProgress(progressPercent);

            if (elapsed >= totalDuration) {
                clearInterval(progressIntervalRef.current!);
                setIsPlayingBack(false);
                audio.pause();
                audio.currentTime = 0;
            }
        }, 100);

        // 延迟 2s 后开始播放录音
        setTimeout(() => {
            audio.play();
        }, prepareDuration);
    };

    useEffect(() => {
        if (recordingComplete && audioUrl) {
            handleUpload();
        }
    }, [recordingComplete, audioUrl]);

    return (
        <div className="record-module">
            <div className="record-container">       
                <div className="record-content">
                    <div className="character-preview" style={{
                            backgroundImage: bpm === 100 ?  
                                `url('https://oss.usecubes.cn/incredibox/assets/images/bg_100.jpg')` : 
                                `url('https://oss.usecubes.cn/incredibox/assets/images/bg_120.jpg')`
                        }}>
                        {/* 添加OC音轨动画预览 */}
                        {ocTrack && (
                            <div className="oc-track-preview">
                                <FramePlayer 
                                    basePath={`${ocTrack.path || 'origin'}/`}
                                    fileName={`${ocTrack.fileDirectory || 'original'}_*.svg`}
                                    frameCount={ocTrack.frames || 8}
                                    fps={bpm / 60 * (ocTrack.frames || 8)}
                                    topColor={ocTrack.topColor}
                                    bottomColor={ocTrack.bottomColor}
                                    startFrame={0}
                                />
                            </div>
                        )}
                    </div>

                    <div className="record-controls">
                        <div className="progress-bar">
                            <div className="progress-fill-wrap">
                                <div 
                                    className="progress-fill"
                                    style={{ 
                                        backgroundImage: `linear-gradient(to right,#FFDD00 0%,#FFDD00 ${ (prepareDuration/totalDuration)*100 }%,#FF0000 ${ (prepareDuration/totalDuration)*100 }%,#FF0000 100%)`
                                    }}
                                >
                                    <div className="fill" style={{ width: `${progress}%` }}></div>
                                </div>
                            </div>
                        </div>

                        <div className="beat-indicators-bar">
                            <div className="countdown-placeholder" style={{ width: `${(prepareDuration/totalDuration)*100}%` }}></div>

                            <div className="beat-indicators" style={{ width: `${(recordingDuration/totalDuration)*100}%` }}>
                                {Array(4).fill(0).map((_, i) => (
                                    <div key={i} className="beat-group">
                                        {Array(4).fill(0).map((_, j) => (
                                            <div 
                                                key={j}
                                                className={`beat ${
                                                    activeBeat === (i*4 + j) ? 'active' : ''
                                                }`}
                                            />
                                        ))}
                                    </div>
                                ))}
                            </div>
                            
                        </div>

                        <div className="beat-texts">
                            <div className="text">{isRecording ?  prepareCountdown : '准备'}</div>
                           <div className="text notice">开始</div>
                           <div className="text">节奏点</div>
                           <div className="text">节奏点</div>
                           <div className="text">节奏点</div>
                           <div className="text notice">结束</div>
                        </div>

                        <div className="tips">
                            {recordingComplete ? (
                                <div className="tips-text">
                                    可以"听一下"不满意可以重新录音
                                </div>
                            ) : (
                                isRecording ? (
                                    <div className="tips-text">
                                        注意保持节奏
                                    </div>
                                ) : (
                                    <div className="tips-text">
                                        做好"准备"，从"开始"录到"结束"
                                    </div>
                                )
                            )}
                        </div>
                    
                        
                    </div>
                </div>

                <div className="buttons">

                        <button className="cancel" onClick={onClose}>{!recordingComplete ? '取消' : '关闭'}</button>


                        {!recordingComplete ? (
                            <button
                                className={`record-button ${isRecording ? 'recording' : ''} ${isBeatActive ? 'onbeat' : ''}`}
                                onClick={startRecording}
                                disabled={isRecording}
                            >
                                {/* <img src="/assets/icons/record.png" alt="record" /> */}
                                {isRecording ? '录音中...' : '开始录音'}
                            </button>
                        ) : (
                            <>
                                {/* <button className="upload-button" onClick={handleUpload}>
                                    上传录音
                                </button> */}
                                <button className="playback-button" onClick={startPlayback} disabled={isPlayingBack || !audioUrl}>
                                    <img src="/assets/icons/play.svg" alt="play" />
                                    听一下
                                </button>
                                <button
                                    className={`record-button ${isRecording ? 'recording' : ''} ${isBeatActive ? 'onbeat' : ''}`}
                                    onClick={startRecording}
                                    disabled={isRecording}
                                >
                                    {/* <img src="/assets/icons/record.png" alt="record" /> */}
                                    {isRecording ? '录音中...' : '重新录制'}
                                </button>
                            </>
                            
                        )}
                </div>



            </div>
            <div className="tip">
            {!ocTrack?.firstRecordedAt ? (
                <span>首次录音后可在24小时内反复录音</span>
            ) : (
                (() => {
                    const firstRecordTime = new Date(ocTrack.firstRecordedAt as Date).getTime();
                    const now = Date.now();
                    const remainingTime = 24 * 60 * 60 * 1000 - (now - firstRecordTime);
                    if (remainingTime > 0) {
                        const hours = Math.floor(remainingTime / (60 * 60 * 1000));
                        const minutes = Math.floor((remainingTime % (60 * 60 * 1000)) / (60 * 1000));
                        return <span>可重复录音，还剩: {hours}小时{minutes}分钟</span>;
                    }
                    return <span>录音已过期</span>;
                })()
            )}
            </div>
        </div>
    );
};

export default RecordModule; 